import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { ButtonPrimary } from 'components';
import {
  INVALID_CONTRACT_STATUS,
  PART_NOT_ELIGIBLE_STATUS,
  REVIEW_STATUS,
  DIFFERENT_ACCOUNT_STATUS,
  IN_WARRANTY_STATUS,
  OUT_OF_WARRANTY_STATUS,
  UNKNOWN_STATUS,
} from 'utils/constants';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { currentRmaState, lookupState, authenticationState, accountState } from 'state/atoms';
import { storageCurrentRmaSelector } from 'state/selectors';
import { isNumber, isOutOfWarrantyWithoutRepairFee } from 'utils/helpers';

const getNewItemException = (currentLookupState, status) => {
  if (status === REVIEW_STATUS) {
    if (currentLookupState.incorrectPartNumber) {
      return 'Incorrect Part Number';
    }

    const { selectedResult } = currentLookupState;

    if (selectedResult.status === UNKNOWN_STATUS) {
      return 'Serial Number Not Found';
    }

    if (selectedResult.status === INVALID_CONTRACT_STATUS) {
      return 'Error in Determining Warranty Status. Please Submit for Review';
    }

    if (selectedResult.status === DIFFERENT_ACCOUNT_STATUS) {
      return 'Serial Number Belongs to Another Customer';
    }

    // When a review is requested, the correct exception is
    // "customer contested warranty status"
    if (selectedResult.status === PART_NOT_ELIGIBLE_STATUS && !currentLookupState.reviewRequested) {
      return 'Part Not RMA Eligible';
    }

    if (isOutOfWarrantyWithoutRepairFee(selectedResult) && !currentLookupState.reviewRequested) {
      return 'Repair fee to be confirmed by Calix';
    }

    return 'Customer Requested Warranty Review';
  }

  return '';
};

const getNewItemStatus = (currentLookupState) => {
  const { selectedResult, incorrectPartNumber, reviewRequested } = currentLookupState;

  // Check the item status
  let status = REVIEW_STATUS;

  if (selectedResult.status === IN_WARRANTY_STATUS && !incorrectPartNumber) {
    status = IN_WARRANTY_STATUS;
  }

  if (
    selectedResult.status === OUT_OF_WARRANTY_STATUS &&
    isNumber(selectedResult.product.outOfWarrantyPrice) &&
    !incorrectPartNumber &&
    !reviewRequested
  ) {
    status = OUT_OF_WARRANTY_STATUS;
  }

  return status;
};

const AddToRma = (props = {}) => {
  const rmaState = useRecoilValue(currentRmaState);
  const setRmaState = useSetRecoilState(storageCurrentRmaSelector);
  const { endCustomerAccountId, hasAllowFeePermission } = useRecoilValue(authenticationState);
  const { defaultCurrencyCode } = useRecoilValue(accountState);
  const [currentLookupState, setCurrentLookupState] = useRecoilState(lookupState);
  const resetLookupState = useResetRecoilState(lookupState);
  const { selectedResult, reviewRequested } = currentLookupState;
  const { outOfWarrantyPrice } = selectedResult.product;

  const status = getNewItemStatus(currentLookupState);
  const acceptedCharges = status === OUT_OF_WARRANTY_STATUS && !reviewRequested;
  const repairFeeNotAllowed = acceptedCharges && outOfWarrantyPrice > 0 && !hasAllowFeePermission;

  const history = useHistory();

  const addToRma = useCallback(() => {
    // Required fields
    const {
      reviewRequested,
      reviewDescription,
      failureCode,
      failureDescription,
      serviceActivity,
      selectedResult,
    } = currentLookupState;

    const requiredFields = [failureCode.value, failureDescription.value, serviceActivity.value];

    if (reviewRequested) {
      requiredFields.push(reviewDescription.value);
    }

    const hasEmptyFields = requiredFields.some((fieldValue) => !fieldValue);
    const isReviewDescriptionValid = !reviewRequested || reviewDescription.value.length > 19;

    if (hasEmptyFields || !isReviewDescriptionValid) {
      // Mark all fields as touched to trigger the validation
      setCurrentLookupState({
        ...currentLookupState,
        failureCode: {
          ...currentLookupState.failureCode,
          touched: true,
        },
        failureDescription: {
          ...currentLookupState.failureDescription,
          touched: true,
        },
        serviceActivity: {
          ...currentLookupState.serviceActivity,
          touched: true,
        },
        reviewDescription: {
          ...currentLookupState.reviewDescription,
          touched: true,
        },
      });
      return;
    }

    const status = getNewItemStatus(currentLookupState);
    const {
      id: partSfId,
      partNumber,
      partName,
      description,
      outOfWarrantyPrice,
    } = selectedResult.product;

    const initialPrice = status === OUT_OF_WARRANTY_STATUS ? outOfWarrantyPrice : 0;

    setRmaState({
      ...rmaState,
      accountId: endCustomerAccountId,
      currencyCode: defaultCurrencyCode,
      items: [
        {
          serialNumber: selectedResult.serialNumber,
          exceptionDescription: getNewItemException(currentLookupState, status),
          failureCode: failureCode.value,
          serviceActivity: serviceActivity.value,
          failureDescription: failureDescription.value,
          reviewDescription: reviewDescription.value,
          acceptedCharges,
          reviewRequested,
          status,
          partSfId,
          partNumber,
          partName,
          description,
          initialPrice,
        },
        ...(rmaState.items || []),
      ],
    });

    resetLookupState();
    history.push('/cases/new/details');
  }, [currentLookupState, rmaState, resetLookupState, setCurrentLookupState, setRmaState, history]);

  return (
    <ButtonPrimary disabled={repairFeeNotAllowed} {...props} onClick={addToRma}>
      Add to RMA
    </ButtonPrimary>
  );
};

export default AddToRma;
